import { auth, db, functions } from "@/config/firebase";
import {
  signInWithEmailLink,
  sendSignInLinkToEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signOut,
  isSignInWithEmailLink,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

export class AuthService {
  static async checkUserExists(email) {
    try {
      // Validate email parameter
      if (!email || typeof email !== "string" || email.trim() === "") {
        console.error("checkUserExists called with invalid email:", email);
        return false;
      }

      const trimmedEmail = email.trim();
      console.log("Checking if user exists for email:", trimmedEmail);

      const methods = await fetchSignInMethodsForEmail(auth, trimmedEmail);
      console.log("Sign-in methods for email:", trimmedEmail, methods);
      return methods.length > 0;
    } catch (error) {
      console.error("Error checking user existence:", error);
      // If we get an error, assume user doesn't exist to be safe
      return false;
    }
  }

  static async sendLoginLink(email, firstName, lastName, redirectUrl = null) {
    try {
      // Validate email parameter
      if (!email || typeof email !== "string" || email.trim() === "") {
        console.error("sendLoginLink called with invalid email:", email);
        return {
          success: false,
          message:
            "Email address is required. Please provide a valid email address.",
        };
      }

      // Trim and validate email format
      const trimmedEmail = email.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return {
          success: false,
          message: "Please enter a valid email address.",
        };
      }

      // Determine the correct redirect URL based on the current domain
      let finalRedirectUrl;
      const currentOrigin = window.location.origin;
      const isLocalhost =
        currentOrigin.includes("localhost") || currentOrigin.includes("127.0.0.1");

      if (redirectUrl) {
        // Use provided redirect URL (for admin login)
        finalRedirectUrl = `${currentOrigin}${redirectUrl}`;
      } else {
        // Default survey redirect URL
        if (currentOrigin.includes("localhost")) {
          finalRedirectUrl = `${currentOrigin}/survey/location-work-arrangements`;
        } else if (currentOrigin.includes("cyberireland.ie")) {
          finalRedirectUrl = `${currentOrigin}/survey/location-work-arrangements`;
        } else if (currentOrigin.includes("csecireland.web.app")) {
          finalRedirectUrl = `${currentOrigin}/survey/location-work-arrangements`;
        } else {
          // Fallback to current origin
          finalRedirectUrl = `${currentOrigin}/survey/location-work-arrangements`;
        }
      }

      // Try to use custom email endpoint first, fallback to default Firebase email
      try {
        const projectId =
          auth?.app?.options?.projectId ||
          functions?.app?.options?.projectId ||
          "csecireland";
        const region = "us-central1";
        const url = `https://${region}-${projectId}.cloudfunctions.net/sendCustomLoginEmailHttp`;

        console.log("Sending custom login email (HTTP) to:", trimmedEmail);
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmedEmail,
            redirectUrl: finalRedirectUrl,
            firstName,
            lastName,
          }),
        });

        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.success === false) {
          throw new Error(payload?.error || payload?.message || "Email send failed");
        }

        // Save email and user data to localStorage
        window.localStorage.setItem("emailForSignIn", trimmedEmail);
        window.localStorage.setItem(
          "pendingUserData",
          JSON.stringify({
            firstName,
            lastName,
            email: trimmedEmail,
          })
        );

        return { success: true, message: "Login link sent to your email!" };
      } catch (customEmailError) {
        console.warn(
          "Custom email function failed:",
          customEmailError
        );

        // In production, do NOT fall back to Firebase Auth's built-in email,
        // otherwise users will receive the standard Firebase template email.
        if (!isLocalhost) {
          return {
            success: false,
            message:
              "We couldn't send the login email right now. Please try again in a moment.",
          };
        }

        // Local development fallback (useful if Functions/Resend isn't configured)
        const actionCodeSettings = {
          url: finalRedirectUrl,
          handleCodeInApp: true,
        };

        console.log("Sending default login link to email:", trimmedEmail);
        await sendSignInLinkToEmail(auth, trimmedEmail, actionCodeSettings);

        // Save email and user data to localStorage
        window.localStorage.setItem("emailForSignIn", trimmedEmail);
        window.localStorage.setItem(
          "pendingUserData",
          JSON.stringify({
            firstName,
            lastName,
            email: trimmedEmail,
          })
        );

        return { success: true, message: "Login link sent to your email!" };
      }
    } catch (error) {
      console.error("Error sending login link:", error);

      // Handle specific Firebase auth errors
      if (error.code === "auth/missing-email") {
        return {
          success: false,
          message:
            "Email address is required. Please provide a valid email address.",
        };
      } else if (error.code === "auth/invalid-email") {
        return {
          success: false,
          message: "Please enter a valid email address.",
        };
      } else if (error.code === "auth/too-many-requests") {
        return {
          success: false,
          message:
            "Too many requests. Please wait a moment before trying again.",
        };
      } else {
        return { success: false, message: error.message };
      }
    }
  }

  static async createAccount(email, firstName, lastName) {
    try {
      // Validate email parameter
      if (!email || typeof email !== "string" || email.trim() === "") {
        console.error("createAccount called with invalid email:", email);
        return {
          success: false,
          message:
            "Email address is required. Please provide a valid email address.",
        };
      }

      // Trim and validate email format
      const trimmedEmail = email.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return {
          success: false,
          message: "Please enter a valid email address.",
        };
      }

      // Generate a temporary password for account creation
      const tempPassword =
        Math.random().toString(36).slice(-10) +
        Math.random().toString(36).slice(-10);

      console.log("Creating account for email:", trimmedEmail);

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        trimmedEmail,
        tempPassword
      );
      const user = userCredential.user;

      // Save user data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        email: trimmedEmail,
        firstName: firstName,
        lastName: lastName,
        createdAt: new Date(),
        surveyData: {
          firstName,
          lastName,
          email: trimmedEmail,
        },
      });

      // Keep the user signed in (don't sign out)
      console.log("Account created and user signed in:", user.email);

      return {
        success: true,
        message: "Account created successfully! You are now signed in.",
        user: user,
      };
    } catch (error) {
      console.error("Error creating account:", error);

      // Handle specific Firebase auth errors
      if (error.code === "auth/email-already-in-use") {
        return {
          success: false,
          message:
            "An account with this email already exists. Please use the login link instead.",
        };
      } else if (error.code === "auth/invalid-email") {
        return {
          success: false,
          message: "Please enter a valid email address.",
        };
      } else if (error.code === "auth/weak-password") {
        return {
          success: false,
          message: "Password is too weak. Please try again.",
        };
      } else {
        return { success: false, message: error.message };
      }
    }
  }

  static async handleEmailLink(emailConfirmationCallback = null) {
    console.log(
      "[handleEmailLink] Called with callback:",
      !!emailConfirmationCallback
    );
    if (isSignInWithEmailLink(auth, window.location.href)) {
      console.log(
        "[handleEmailLink] Detected sign-in email link in URL:",
        window.location.href
      );
      let email = window.localStorage.getItem("emailForSignIn");
      let pendingUserData = window.localStorage.getItem("pendingUserData");
      console.log("[handleEmailLink] emailForSignIn from localStorage:", email);
      console.log(
        "[handleEmailLink] pendingUserData from localStorage:",
        pendingUserData
      );

      if (!email) {
        console.log(
          "[handleEmailLink] No email in localStorage, using modal/callback"
        );
        // Always use modal approach - never fall back to window.prompt
        if (emailConfirmationCallback) {
          // Use provided callback to get email from modal
          email = await emailConfirmationCallback();
          console.log("[handleEmailLink] Got email from callback:", email);
          if (!email) {
            console.log("[handleEmailLink] Email confirmation cancelled");
            return { success: false, message: "Email confirmation cancelled" };
          }
        } else {
          // Use global modal system if no callback provided
          email = await this.getEmailFromGlobalModal();
          console.log("[handleEmailLink] Got email from global modal:", email);
          if (!email) {
            console.log("[handleEmailLink] Email confirmation cancelled");
            return { success: false, message: "Email confirmation cancelled" };
          }
        }
      }

      try {
        console.log(
          "[handleEmailLink] Attempting signInWithEmailLink for:",
          email
        );
        await signInWithEmailLink(auth, email, window.location.href);
        console.log(
          "[handleEmailLink] signInWithEmailLink succeeded. Current user:",
          auth.currentUser
        );

        // If we have pending user data, save it to Firestore
        if (pendingUserData) {
          const userData = JSON.parse(pendingUserData);
          const user = auth.currentUser;
          console.log("[handleEmailLink] pendingUserData exists, user:", user);

          if (user) {
            await setDoc(
              doc(db, "users", user.uid),
              {
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                createdAt: new Date(),
                surveyData: userData,
              },
              { merge: true }
            );
            console.log(
              "[handleEmailLink] User data saved to Firestore for UID:",
              user.uid
            );
          } else {
            console.warn(
              "[handleEmailLink] No currentUser after signInWithEmailLink!"
            );
          }
        }

        // Clean up localStorage
        window.localStorage.removeItem("emailForSignIn");
        window.localStorage.removeItem("pendingUserData");
        console.log("[handleEmailLink] Cleaned up localStorage");

        return { success: true, message: "Successfully signed in!" };
      } catch (error) {
        console.error(
          "[handleEmailLink] Error signing in with email link:",
          error
        );
        return { success: false, message: error.message };
      }
    }
    console.log("[handleEmailLink] No email link detected in URL");
    return { success: false, message: "No email link detected" };
  }

  // Global modal system for email confirmation
  static getEmailFromGlobalModal() {
    return new Promise((resolve) => {
      // Create modal elements
      const modalOverlay = document.createElement("div");
      modalOverlay.className = "email-confirmation-modal-overlay";
      modalOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      `;

      const modalContent = document.createElement("div");
      modalContent.className = "email-confirmation-modal-content";
      modalContent.style.cssText = `
        background: white;
        border-radius: 8px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        padding: 0;
      `;

      const modalHeader = document.createElement("div");
      modalHeader.style.cssText = `
        padding: 1.5rem;
        border-bottom: 1px solid #e1e5e9;
      `;
      modalHeader.innerHTML =
        '<h2 style="margin: 0; color: #2c3e50; font-size: 1.25rem;">Email Confirmation Required</h2>';

      const modalBody = document.createElement("div");
      modalBody.style.cssText = `
        padding: 1.5rem;
      `;
      modalBody.innerHTML = `
        <p style="margin: 0 0 1rem 0; color: #6c757d; line-height: 1.5;">
          Please provide your email address to complete the sign-in process.
        </p>
        <div style="margin-bottom: 1rem;">
          <label for="confirmEmail" style="display: block; margin-bottom: 0.5rem; font-weight: 500; color: #2c3e50;">Email Address</label>
          <input
            id="confirmEmail"
            type="email"
            placeholder="Enter your email address"
            style="width: 100%; padding: 0.75rem; border: 1px solid #ddd; border-radius: 6px; font-size: 1rem; box-sizing: border-box;"
            required
          />
        </div>
      `;

      const modalFooter = document.createElement("div");
      modalFooter.style.cssText = `
        padding: 1rem 1.5rem;
        border-top: 1px solid #e1e5e9;
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
      `;

      const cancelButton = document.createElement("button");
      cancelButton.textContent = "Cancel";
      cancelButton.style.cssText = `
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
        background-color: #6c757d;
        color: white;
        transition: background-color 0.2s ease;
      `;

      const confirmButton = document.createElement("button");
      confirmButton.textContent = "Confirm";
      confirmButton.style.cssText = `
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
        background-color: #42b883;
        color: white;
        transition: background-color 0.2s ease;
      `;

      // Add hover effects
      cancelButton.onmouseenter = () =>
        (cancelButton.style.backgroundColor = "#5a6268");
      cancelButton.onmouseleave = () =>
        (cancelButton.style.backgroundColor = "#6c757d");
      confirmButton.onmouseenter = () =>
        (confirmButton.style.backgroundColor = "#3aa876");
      confirmButton.onmouseleave = () =>
        (confirmButton.style.backgroundColor = "#42b883");

      // Event handlers
      const emailInput = modalBody.querySelector("#confirmEmail");

      const handleConfirm = () => {
        const email = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
          document.body.removeChild(modalOverlay);
          resolve(email);
        } else {
          emailInput.style.borderColor = "#dc3545";
          emailInput.focus();
        }
      };

      const handleCancel = () => {
        document.body.removeChild(modalOverlay);
        resolve(null);
      };

      const handleKeyPress = (e) => {
        if (e.key === "Enter") {
          handleConfirm();
        } else if (e.key === "Escape") {
          handleCancel();
        }
      };

      // Add event listeners
      confirmButton.onclick = handleConfirm;
      cancelButton.onclick = handleCancel;
      emailInput.onkeypress = handleKeyPress;
      modalOverlay.onclick = (e) => {
        if (e.target === modalOverlay) {
          handleCancel();
        }
      };

      // Assemble modal
      modalFooter.appendChild(cancelButton);
      modalFooter.appendChild(confirmButton);
      modalContent.appendChild(modalHeader);
      modalContent.appendChild(modalBody);
      modalContent.appendChild(modalFooter);
      modalOverlay.appendChild(modalContent);

      // Add to DOM and focus input
      document.body.appendChild(modalOverlay);
      emailInput.focus();
    });
  }

  static async getUserData(userId) {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  }

  static async signOut() {
    try {
      await signOut(auth);
      return { success: true, message: "Successfully signed out!" };
    } catch (error) {
      console.error("Error signing out:", error);
      return { success: false, message: error.message };
    }
  }

  static getCurrentUser() {
    return auth.currentUser;
  }

  static onAuthStateChanged(callback) {
    return auth.onAuthStateChanged(callback);
  }
}
